import { AppAreaConfig, DomainConfig } from './@types'
import { PaywallRoute, UpsellRoute } from '@/@types/router-types'
import { APP_AREAS } from '@/funnels/areas'

// Runtime type guard
export const isAppAreaConfig = (config: unknown): config is AppAreaConfig => {
	return (
		typeof config === 'object' && config !== null && 'appArea' in config && 'landing' in config && 'funnel' in config
	)
}

// Default values from main funnel for redirect funnels fallbacks
const DEFAULT_PAYWALL_CONFIG: { routeName: PaywallRoute } = {
	routeName: 'plan-main',
}

const DEFAULT_UPSELL_CONFIG: { routeNames: UpsellRoute[] } = {
	routeNames: ['upsell-static-subscription-activated', 'upsell-f'],
}

// Generic helper to get config value with fallback
const getConfigValue = <T extends object, K extends keyof T>(obj: T | undefined, key: K, defaultValue: T[K]): T[K] => {
	return obj && key in obj ? obj[key] : defaultValue
}

// Helper functions to get config values with fallbacks
export const getPaywallRouteName = (config: AppAreaConfig): string =>
	getConfigValue(config.paywall, 'routeName', DEFAULT_PAYWALL_CONFIG.routeName)

export const getUpsellRouteNames = (config: AppAreaConfig): string[] =>
	getConfigValue(config.upsell, 'routeNames', DEFAULT_UPSELL_CONFIG.routeNames)

// Route generation functions
function generateRoutesFromAppArea(appAreaConfig: AppAreaConfig) {
	const routes: Array<{
		path: string
		name: string
		component: () => Promise<unknown>
		meta?: { isLanding?: boolean }
	}> = []

	if (appAreaConfig.landing?.route) {
		routes.push({
			path: appAreaConfig.landing.route.path,
			name: appAreaConfig.landing.route.name,
			component: () => import('@/modules/Landing/LandingView.vue'),
			meta: {
				isLanding: true,
			},
		})
	}

	if (appAreaConfig.funnel?.route) {
		routes.push({
			path: appAreaConfig.funnel.route.path,
			name: appAreaConfig.funnel.route.name,
			component: () => import('@/modules/Onboarding/OnboardingView.vue'),
		})
	}

	return routes
}

export function generateDomainConfig(
	appAreas: AppAreaConfig[],
	manualPathnames?: { [pathname: string]: AppAreaConfig },
): DomainConfig {
	const config: DomainConfig = { ...manualPathnames }

	for (const appArea of appAreas) {
		if (appArea.landing?.route?.path) {
			const pathname = appArea.landing.route.path
			config[pathname] = appArea
		}
	}

	return config
}

export function funnelRoutes(domainPathnames?: DomainConfig) {
	const areas = domainPathnames
		? [
				...new Map(
					Object.values(domainPathnames)
						.filter(isAppAreaConfig)
						.map((a) => [a.appArea, a]),
				).values(),
			]
		: APP_AREAS

	const allRoutes: Array<{
		path: string
		name: string
		component: () => Promise<unknown>
		meta?: { isLanding?: boolean }
	}> = []

	areas.forEach((appAreaConfig) => {
		const routes = generateRoutesFromAppArea(appAreaConfig)
		allRoutes.push(...routes)
	})

	return allRoutes
}
